import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";
import { FORM } from "@/constants/testIds";

const STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Chandigarh","Jammu & Kashmir","Ladakh",
];

const BUSINESS_TYPES = ["Retailer", "Wholesaler", "Distributor", "Boutique Owner", "Reseller", "Fashion Store", "Multi-brand Store", "E-commerce Reseller"];
const MONTHLY = ["< ₹1 Lakh", "₹1-5 Lakhs", "₹5-10 Lakhs", "₹10-25 Lakhs", "₹25 Lakhs+"];

export default function LeadForm({ type = "contact", showFields = [], submitLabel = "Submit Enquiry", presetMessage = "" }) {
  const [form, setForm] = useState({
    type,
    company_name: "",
    contact_person: "",
    phone: "",
    email: "",
    gst: "",
    business_type: "",
    state: "",
    city: "",
    categories: [],
    brands: [],
    monthly_purchase: "",
    message: presetMessage,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const has = (f) => showFields.length === 0 || showFields.includes(f);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_person || !form.phone) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setSuccess(true);
      toast.success("Enquiry received. Our team will contact you within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-12" data-testid="lead-form-success">
        <div className="mx-auto h-16 w-16 rounded-full bg-gold/10 text-gold flex items-center justify-center mb-5">
          <CheckCircle2 size={30} strokeWidth={1.5} />
        </div>
        <h3 className="font-serif text-3xl text-foreground">Enquiry submitted!</h3>
        <p className="mt-3 text-foreground/70 max-w-md mx-auto">Thank you for connecting with Hermanos Industries. Our business team will reach out within 24 hours.</p>
        <a href="/" className="mt-6 inline-block text-xs tracking-wider uppercase text-gold hover:underline">Back to home →</a>
      </div>
    );
  }

  const FORM_TESTID_BY_TYPE = {
    catalogue: FORM.catalogueForm,
    quotation: FORM.quotationForm,
    contact: FORM.contactForm,
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-5"
      data-testid={FORM_TESTID_BY_TYPE[type] || FORM.contactForm}
    >
      <div className="grid md:grid-cols-2 gap-4">
        {has("company_name") && (
          <div>
            <Label className="text-xs tracking-wider uppercase text-foreground/70">Company / Shop Name</Label>
            <Input data-testid={FORM.companyName} className="mt-2 rounded-sm bg-card" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
          </div>
        )}
        {has("contact_person") && (
          <div>
            <Label className="text-xs tracking-wider uppercase text-foreground/70">Contact Person <span className="text-gold">*</span></Label>
            <Input data-testid={FORM.contactPerson} className="mt-2 rounded-sm bg-card" required value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} />
          </div>
        )}
        {has("phone") && (
          <div>
            <Label className="text-xs tracking-wider uppercase text-foreground/70">Mobile Number <span className="text-gold">*</span></Label>
            <Input data-testid={FORM.phone} type="tel" className="mt-2 rounded-sm bg-card" required placeholder="+91" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
        )}
        {has("email") && (
          <div>
            <Label className="text-xs tracking-wider uppercase text-foreground/70">Email</Label>
            <Input data-testid={FORM.email} type="email" className="mt-2 rounded-sm bg-card" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
        )}
        {has("gst") && (
          <div>
            <Label className="text-xs tracking-wider uppercase text-foreground/70">GST Number (Optional)</Label>
            <Input data-testid={FORM.gst} className="mt-2 rounded-sm bg-card" value={form.gst} onChange={(e) => set("gst", e.target.value)} />
          </div>
        )}
        {has("business_type") && (
          <div>
            <Label className="text-xs tracking-wider uppercase text-foreground/70">Business Type</Label>
            <Select value={form.business_type} onValueChange={(v) => set("business_type", v)}>
              <SelectTrigger data-testid={FORM.businessType} className="mt-2 rounded-sm bg-card">
                <SelectValue placeholder="Select business type" />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {has("state") && (
          <div>
            <Label className="text-xs tracking-wider uppercase text-foreground/70">State</Label>
            <Select value={form.state} onValueChange={(v) => set("state", v)}>
              <SelectTrigger data-testid={FORM.state} className="mt-2 rounded-sm bg-card">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {has("city") && (
          <div>
            <Label className="text-xs tracking-wider uppercase text-foreground/70">City</Label>
            <Input data-testid={FORM.city} className="mt-2 rounded-sm bg-card" value={form.city} onChange={(e) => set("city", e.target.value)} />
          </div>
        )}
        {has("monthly_purchase") && (
          <div className="md:col-span-2">
            <Label className="text-xs tracking-wider uppercase text-foreground/70">Estimated Monthly Purchase</Label>
            <Select value={form.monthly_purchase} onValueChange={(v) => set("monthly_purchase", v)}>
              <SelectTrigger data-testid={FORM.monthlyPurchase} className="mt-2 rounded-sm bg-card">
                <SelectValue placeholder="Select estimated volume" />
              </SelectTrigger>
              <SelectContent>
                {MONTHLY.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {has("message") && (
        <div>
          <Label className="text-xs tracking-wider uppercase text-foreground/70">Message / Requirements</Label>
          <Textarea
            data-testid={FORM.message}
            className="mt-2 rounded-sm bg-card min-h-[110px]"
            placeholder="Tell us about your business, product interests, and any specific requirements…"
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
          />
        </div>
      )}
      <Button
        data-testid={FORM.submitBtn}
        type="submit"
        disabled={submitting}
        className="w-full h-12 rounded-sm bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-widest uppercase text-xs"
      >
        {submitting ? (<><Loader2 size={16} className="animate-spin mr-2" /> Submitting…</>) : submitLabel}
      </Button>
      <p className="text-[0.7rem] text-foreground/50 text-center">
        By submitting you agree to be contacted by our sales team. We respect your business privacy.
      </p>
    </form>
  );
}
